import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import * as echarts from 'echarts';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DetailsDialog from './DetailsDialog';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

const Pie = ({
  chartId,
  data,
  top,
  loading,
  customColors,
  showLegend,
  showDetailsDialog,
  dialogButtonLabel,
  dialogTitle,
  pieCenterLabel,
  pieTitle,
  pieSubTitle,
  tableHead
}) => {
  const chartInstanceRef = useRef(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('analytics');

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (loading || !top) return;

    const el = document.getElementById(`${chartId}-canvas`);
    if (!el) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(el);
    }

    const chartInstance = chartInstanceRef.current;

    const calculatedTotal =
      data?.reduce((acc, cur) => acc + cur.count, 0) || 0;

    setTotal(calculatedTotal);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const value = Number(params.value) || 0;

          return `
            <div style="max-width:250px; white-space:normal; word-break:break-word;">
              ${params.marker}
              <strong>${params.name}</strong><br/>
              Count: ${value}<br/>
              Share: ${
                calculatedTotal
                  ? ((value / calculatedTotal) * 100).toFixed(2)
                  : 0
              }%
            </div>
          `;
        }
      },
      legend: {
        show: showLegend,
        bottom: '-5px',
        left: 'center',
        formatter: (name) => {
          const item = top?.find((d) => d.label === name);
          return `${name} (${item?.count})`;
        }
      },
      series: [
        {
          name: 'Status',
          type: 'pie',
          radius: ['50%', '80%'],
          minAngle: 0.1,
          padAngle: 3,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false },
          labelLine: { show: false },
          emphasis: {
            scale: true,
            scaleSize: 10
          },
          data: top?.map((item) => ({
            value: item.count,
            name: item.label,
            itemStyle: {
              color: customColors && customColors[item.label]
            }
          }))
        }
      ]
    };

    chartInstance.setOption(option);

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        chartInstance.resize();
      }, 0);
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [top, data, loading, showLegend, customColors, chartId]);

  return (
    <Box sx={{}}>
      <Typography
        variant="h6"
        sx={{ fontSize: 20, fontWeight: 500, textAlign: 'center' }}
      >
        {pieTitle}
      </Typography>

      {pieSubTitle && (
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            textAlign: 'center',
            color: '#86878C'
          }}
        >
          {pieSubTitle}
        </Typography>
      )}

      <Box>
        {loading ? (
          <Box
            sx={{
              width: '100%',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {data.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 260
                }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                />
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('NoData')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative', height: 280 }}>
                {/* render pie chart using id */}
                <Box
                  id={`${chartId}-canvas`}
                  sx={{
                    width: '100%',
                    height: 250,
                    position: 'relative',
                  }}
                />

                {/* Center Label */}
                <Box
                  sx={{
                    pointerEvents: 'none',
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    top: '-36px',
                  }}
                >
                  <Box sx={{ textAlign: 'center', borderRadius: '100%', p: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: 26,
                        letterSpacing: '0.5px',
                        fontFamily: 'Outfit'
                      }}
                    >
                      {total}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'text.secondary'
                      }}
                    >
                      {pieCenterLabel}
                    </Typography>
                  </Box>
                </Box>

                {/* Details Button */}
                {showDetailsDialog && data.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<ListAltIcon fontSize="small" />}
                    onClick={handleClickOpen}
                    sx={{
                      minWidth: 0,
                      maxWidth: 200,
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      marginLeft: '50%',
                      transform: 'translateX(-50%)',
                      '&:hover': { color: 'black' },
                      position: 'absolute',
                      top: '90%'
                    }}
                  >
                    {dialogButtonLabel ? dialogButtonLabel : 'Details'}
                  </Button>
                )}

                {/* Dialog */}
                {showDetailsDialog && (
                  <DetailsDialog
                    open={open}
                    onClose={handleClose}
                    title={dialogTitle}
                    data={data}
                    tableHead={tableHead}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Pie;